import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

interface CarouselPosition {
  carouselRef: React.RefObject<HTMLDivElement | null>;
  currentIndex: number;
  scrollToIndex: (index: number) => void;
  handleKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

function getItems(container: HTMLDivElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>("[data-carousel-item]"),
  );
}

function getItemPosition(items: HTMLElement[], index: number): number {
  const firstItem = items[0];
  const targetItem = items[index];

  if (!firstItem || !targetItem) {
    return 0;
  }

  return targetItem.offsetLeft - firstItem.offsetLeft;
}

export function useCarouselPosition(itemCount: number): CarouselPosition {
  const carouselRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const currentIndexRef = useRef(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const updateCurrentIndex = useCallback(() => {
    const container = carouselRef.current;

    if (!container) {
      return;
    }

    const items = getItems(container);

    if (items.length === 0) {
      return;
    }

    const nearestIndex = items.reduce((nearest, _, index) => {
      const nearestDistance = Math.abs(
        getItemPosition(items, nearest) - container.scrollLeft,
      );
      const currentDistance = Math.abs(
        getItemPosition(items, index) - container.scrollLeft,
      );

      return currentDistance < nearestDistance ? index : nearest;
    }, 0);

    currentIndexRef.current = nearestIndex;
    setCurrentIndex(nearestIndex);
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = carouselRef.current;

      if (!container || itemCount === 0) {
        return;
      }

      const safeIndex = Math.max(0, Math.min(index, itemCount - 1));
      const items = getItems(container);
      const left = getItemPosition(items, safeIndex);

      currentIndexRef.current = safeIndex;
      setCurrentIndex(safeIndex);

      if (typeof container.scrollTo === "function") {
        container.scrollTo({ left, behavior: "smooth" });
      } else {
        container.scrollLeft = left;
      }
    },
    [itemCount],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      let nextIndex = currentIndexRef.current;

      if (event.key === "ArrowRight") {
        nextIndex += 1;
      } else if (event.key === "ArrowLeft") {
        nextIndex -= 1;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = itemCount - 1;
      } else {
        return;
      }

      event.preventDefault();
      scrollToIndex(nextIndex);
    },
    [itemCount, scrollToIndex],
  );

  useEffect(() => {
    const container = carouselRef.current;

    if (!container) {
      return;
    }

    const handleScroll = () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(updateCurrentIndex);
    };

    const preserveCurrentPosition = () => {
      const items = getItems(container);
      const left = getItemPosition(items, currentIndexRef.current);

      if (typeof container.scrollTo === "function") {
        container.scrollTo({ left, behavior: "auto" });
      } else {
        container.scrollLeft = left;
      }

      updateCurrentIndex();
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(preserveCurrentPosition);

    resizeObserver?.observe(container);
    getItems(container).forEach((item) => resizeObserver?.observe(item));

    return () => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver?.disconnect();

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [itemCount, updateCurrentIndex]);

  useEffect(() => {
    if (currentIndexRef.current >= itemCount) {
      currentIndexRef.current = Math.max(itemCount - 1, 0);
      setCurrentIndex(currentIndexRef.current);
    }
  }, [itemCount]);

  return {
    carouselRef,
    currentIndex,
    scrollToIndex,
    handleKeyDown,
  };
}
